import React, { useEffect, useState, useCallback } from "react";
import { Plus, Camera } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, Pipeline, EmptyState } from "@/components/Bits";
import TaskDrawer from "@/components/TaskDrawer";
import TaskFormDialog from "@/components/TaskFormDialog";
import { DIVISION_PIPELINES } from "@/lib/ui";
import api from "@/lib/api";

const THUMBS = [
  "https://images.pexels.com/photos/33714920/pexels-photo-33714920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/16708243/pexels-photo-16708243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1586732711591-12c04655338f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxiZWhpbmQlMjB0aGUlMjBzY2VuZXMlMjBwaG90byUyMHNob290fGVufDB8fHx8MTc4OTc2MzQ0NHww&ixlib=rb-4.1.0&q=85",
];

export default function Production() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => api.get("/tasks", { params: { division: "Production" } }).then((r) => setTasks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/team").then((r) => setTeam(r.data));
  }, []);

  return (
    <div>
      <PageHeader
        title="Production / Photography / Shoots"
        subtitle="Shoot planning, crew, assets & delivery"
        actions={
          <button data-testid="new-production-button" onClick={() => setFormOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Shoot
          </button>
        }
      />
      <div className="p-8 space-y-6">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
          <Pipeline steps={DIVISION_PIPELINES["Production"]} testId="production-pipeline" />
        </div>

        {tasks.length === 0 ? (
          <EmptyState icon={Camera} title="No production projects" hint="Plan your first shoot." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasks.map((t, i) => (
              <div key={t.id} data-testid={`production-card-${t.id}`} onClick={() => { setDrawerId(t.id); setDrawerOpen(true); }} className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden hover:border-white/20 transition-colors cursor-pointer group">
                <div className="h-36 overflow-hidden bg-black">
                  <img src={THUMBS[i % THUMBS.length]} alt={t.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-zinc-500">{t.ref}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <h3 className="font-display text-base font-medium mt-2">{t.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{t.brand}</p>
                  <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-zinc-600">Shoot:</span> <span className="text-zinc-300">{t.shoot_date || "TBD"}</span></div>
                    <div><span className="text-zinc-600">Crew:</span> <span className="text-zinc-300">{t.crew || "—"}</span></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <TaskDrawer taskId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
      <TaskFormDialog open={formOpen} onOpenChange={setFormOpen} clients={clients} team={team} onCreated={load} defaultDivision="Production" />
    </div>
  );
}
